import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@arcadeai/design-system/components/ui/atoms/accordion";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Link } from "@tanstack/react-router";
import { ExternalLink, KeyRound, Package, Zap } from "lucide-react";
import type { Tool } from "@/app/tools/types";

type ToolDetailsProps = {
  tool: Tool;
  hasScopes: boolean;
};

export const ToolDetails = ({ tool, hasScopes }: ToolDetailsProps) => (
  <div className="space-y-4">
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Package className="h-5 w-5 text-muted-foreground" />
        <span className="text-muted-foreground text-sm">
          Toolkit: {tool.toolkit.name}
        </span>
      </div>
      <div className="flex items-center space-x-2">
        <Zap className="h-5 w-5 text-muted-foreground" />
        <span className="text-muted-foreground text-sm">
          {tool.fully_qualified_name}
        </span>
      </div>
    </div>
    <Accordion
      className="w-full"
      defaultValue={[tool?.input?.parameters ? "inputs" : "output"]}
    >
      {!!tool?.input?.parameters && (
        <AccordionItem value="inputs">
          <AccordionTrigger>
            <div className="flex items-center">Inputs</div>
          </AccordionTrigger>
          <AccordionContent>
            <ul className="space-y-4">
              {tool.input.parameters.map((param) => (
                <li className="border-b pb-4 last:border-b-0" key={param.name}>
                  <div className="font-semibold">{param.name}</div>
                  <div className="text-muted-foreground text-sm">
                    {param.description}
                  </div>
                  <div className="mt-1 text-sm">
                    <span
                      className={`font-medium ${param.required ? "text-red-500" : "text-green-500"}`}
                    >
                      {param.required ? "Required" : "Optional"}
                    </span>
                    {!!param.value_schema.enum && (
                      <span className="ml-2 text-muted-foreground">
                        Options: {param.value_schema.enum.join(", ")}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      )}

      <AccordionItem value="output">
        <AccordionTrigger>
          <div className="flex items-center">Output</div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2">
            {!!tool.output?.description && (
              <div>
                <span className="font-semibold">Description: </span>
                {tool.output.description}
              </div>
            )}
            {!!tool.output?.available_modes && (
              <div>
                <span className="font-semibold">Available Modes: </span>
                {tool.output.available_modes.join(", ")}
              </div>
            )}
            {Boolean(tool.output?.value_schema?.val_type) && (
              <div>
                <span className="font-semibold">Value Type: </span>
                {tool.output?.value_schema?.val_type}
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>

      {Object.keys(tool.requirements ?? {}).length > 0 && (
        <AccordionItem value="requirements">
          <AccordionTrigger>
            <div className="flex items-center">Requirements</div>
          </AccordionTrigger>
          <AccordionContent>
            {!!tool.requirements?.authorization && (
              <div className="space-y-2">
                <div>
                  <Link
                    className="flex items-center gap-2"
                    from={
                      tool.requirements?.authorization.provider_type ===
                      "oauth2"
                        ? "/orgs/$organizationId/projects/$projectId/auth/oauth"
                        : "/orgs/$organizationId/projects/$projectId/auth/secrets"
                    }
                    search={{
                      name: tool.requirements?.authorization.provider_id,
                    }}
                    to="."
                  >
                    <span className="font-semibold">Provider: </span>
                    <Badge
                      className="flex items-center gap-2"
                      variant="secondary"
                    >
                      <span>
                        {tool.requirements?.authorization.provider_id}
                      </span>
                      <ExternalLink className="h-3 w-3" />
                    </Badge>
                  </Link>
                </div>
                <div>
                  <span className="font-semibold">Type: </span>
                  {tool.requirements?.authorization.provider_type}
                </div>
                {!!hasScopes && (
                  <div>
                    <span className="font-semibold">Scopes: </span>
                    {tool.requirements?.authorization.oauth2?.scopes?.join(
                      ", "
                    )}
                  </div>
                )}
              </div>
            )}
            {!!tool.requirements?.secrets && (
              <div className="flex flex-wrap gap-3">
                <span className="font-semibold">Secrets: </span>
                {tool.requirements?.secrets.map((secret) => (
                  <Badge
                    className="flex w-fit items-center gap-2.5"
                    key={secret.key}
                    variant="secondary"
                  >
                    <KeyRound className="h-3 w-3" />
                    {secret.key}
                  </Badge>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  </div>
);
